/* oxlint-disable tailwindcss/no-unknown-classes -- Semantic duration tokens are not resolved from the CSS theme. */
import type React from "react";

import type { PollDto, PollResultDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { CheckDone02Icon } from "@/components/Icons/CheckDone02Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";
import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

interface CommunityPollResultSectionProps {
  poll: PollDto;
  results: PollResultDto[];
}

export function CommunityPollResultSection({ poll, results }: CommunityPollResultSectionProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t } = useTranslation();
  const [isClearVotesConfirmModalOpened, clearVotesConfirmModalHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const deletePollVotesMutation = pollMutations.useDeletePollVotes();

  const amountTotalResults = results.reduce((acc, result) => acc + result.count, 0);
  const isPollEnded = poll.endsAt && new Date(poll.endsAt) < clockDate;

  return (
    <>
      <Stack gap="md">
        {poll.allowMultipleAnswers && !isPollEnded && (
          <Inline gap="xs" align="center">
            <CheckDone02Icon size={16} />
            <span className="text-caption">{t("component.community-poll.form.label")}</span>
          </Inline>
        )}
        {poll.canRespond === "alreadyAnswered" && (
          <Button
            variant="ghostPrimary"
            data-testid="community-poll-clear-own-votes-btn"
            onClick={clearVotesConfirmModalHandler.setTrue}
          >
            {t("component.community-poll.button.clear-votes")}
          </Button>
        )}

        <ul className="flex w-full flex-col gap-space-sm" data-testid="community-poll-result-section">
          {results.map((result) => (
            <Card as="li" borderTone="secondary" clip key={result.answer.id} data-testid="community-poll-result-option">
              <div className="relative z-20 flex w-full items-center justify-between">
                <Inline gap="sm" align="center">
                  {result.voters.find((voter) => voter.id === sessionUser.id) && (
                    <span
                      role="img"
                      aria-label={t("component.community-poll.result.your-vote")}
                      className="flex size-icon-inline items-center justify-center rounded-full bg-background-default text-text-brand-secondary"
                      data-testid="community-poll-own-vote-indicator"
                    >
                      <CheckIcon size={12} />
                    </span>
                  )}
                  <span className="text-body">{result.answer.text}</span>
                  {result.hasWon && result.count > 0 && (
                    <span role="img" aria-label={t("component.community-poll.result.winning-option")}>
                      <Star01Icon appearance="filled" size={20} tone="rating" />
                    </span>
                  )}
                </Inline>

                <Inline align="center" wrap={false}>
                  {result.voters.length > 0 && (
                    <UserAvatarList limit={3} images={result.voters.map((voter) => voter.avatar)} />
                  )}
                  <span className="flex w-icon-prominent justify-center text-body-emphasis">{result.count}</span>
                </Inline>
              </div>

              {/* Progress background */}
              <RuntimeWidth width={amountTotalResults === 0 ? 0 : `${(result.count / amountTotalResults) * 100}%`}>
                {(widthProps) => (
                  <div
                    {...widthProps}
                    className="duration-slow absolute top-0 left-0 z-0 flex h-full animate-in items-center bg-background-tertiary/50 ease-out slide-in-from-left-full"
                  />
                )}
              </RuntimeWidth>
            </Card>
          ))}
        </ul>
      </Stack>

      <ConfirmModal
        tone="error"
        data-testid="community-poll-clear-own-votes-confirm-modal"
        title={t("component.community-poll.clear-votes-modal.title")}
        description={t("component.community-poll.clear-votes-modal.description")}
        isOpened={isClearVotesConfirmModalOpened}
        onOpenChange={clearVotesConfirmModalHandler.set}
        shouldCloseOnEsc
        onReject={clearVotesConfirmModalHandler.setFalse}
        rejectBtnProps={{
          text: t("component.community-poll.clear-votes-modal.button.cancel"),
        }}
        onResolve={async () => {
          await deletePollVotesMutation.mutateAsync(poll.id);
          clearVotesConfirmModalHandler.setFalse();
        }}
        resolveBtnProps={{
          text: t("component.community-poll.clear-votes-modal.button.confirm"),
          "data-testid": "confirm-btn",
        }}
        isLoading={deletePollVotesMutation.isPending}
      />
    </>
  );
}
